import { memo, useEffect, useRef } from "react";
import { motion } from "framer-motion";

/**
 * NexoCore — Canvas-based neural orb
 * Portado diretamente do mesmo código usado na landing page oficial (landing-page/index.html).
 * Representa o "cérebro" do Nexo: núcleo luminoso + sinapses orgânicas com control points
 * animados, branch pathways, traveling pulses, partículas e órbitas dashed.
 */

/**
 * Estados:
 * - idle: azul claro metálico estável (cérebro em repouso)
 * - listening/processing/speaking: filtro animado cicla entre AZUL → BRANCO → VERMELHO
 *   reproduzindo variação de "cor conforme o contexto da conversa"
 */
const STATE_CONFIG = {
  idle: {
    // Azul claro metálico — filtro estático, sem ciclagem
    filterStatic: "saturate(0.95) brightness(1.08) contrast(1.05)",
    filterAnim: null,
    vibrate: false,
    speedMultiplier: 0.45,
    intensityMultiplier: 1.0,
  },
  listening: {
    // Pulsação sutil azul ↔ branco (recebendo info, cérebro "atento")
    filterStatic: null,
    filterAnim: {
      values: [
        "saturate(1.2) brightness(1.15) hue-rotate(0deg)",
        "saturate(0.9) brightness(1.3) hue-rotate(0deg)",
        "saturate(1.2) brightness(1.15) hue-rotate(0deg)",
      ],
      duration: 3.5,
    },
    vibrate: false,
    speedMultiplier: 0.75,
    intensityMultiplier: 1.12,
  },
  processing: {
    // Variação azul → branco → vermelho (pensando, processando info variada)
    filterStatic: null,
    filterAnim: {
      values: [
        "saturate(1.25) brightness(1.15) hue-rotate(0deg)",
        "saturate(0.9) brightness(1.35) hue-rotate(0deg)",
        "saturate(1.4) brightness(1.25) hue-rotate(-45deg)",
        "saturate(0.9) brightness(1.3) hue-rotate(-20deg)",
        "saturate(1.25) brightness(1.15) hue-rotate(0deg)",
      ],
      duration: 4,
    },
    vibrate: false,
    speedMultiplier: 2.2, // mantido — user aprovou "pensando tá bacana"
    intensityMultiplier: 1.18,
  },
  speaking: {
    // Mesma paleta variada (azul ↔ branco ↔ vermelho) mas ciclo mais rápido
    filterStatic: null,
    filterAnim: {
      values: [
        "saturate(1.3) brightness(1.2) hue-rotate(0deg)",
        "saturate(0.85) brightness(1.4) hue-rotate(0deg)",
        "saturate(1.45) brightness(1.3) hue-rotate(-55deg)",
        "saturate(0.9) brightness(1.35) hue-rotate(-15deg)",
        "saturate(1.3) brightness(1.2) hue-rotate(0deg)",
      ],
      duration: 3,
    },
    vibrate: true,
    speedMultiplier: 1.5, // reduzido de 2.6 → 1.5 (mais uniforme com processing)
    intensityMultiplier: 1.25,
  },
};

function initNeuralOrb(canvas, opts = {}) {
  const ctx = canvas.getContext("2d");
  const W = canvas.width;
  const H = canvas.height;
  const cx = W / 2;
  const cy = H / 2;
  const S = opts.scale || 1;
  const stateRef = opts.stateRef; // mutable ref read on each frame

  // Helper: cluster angles — NOT evenly distributed (look orgânico)
  function clusterAngles(count) {
    const angles = [];
    const numClusters = 3 + Math.floor(Math.random() * 2);
    const clusterCenters = [];
    for (let i = 0; i < numClusters; i++) {
      clusterCenters.push(Math.random() * Math.PI * 2);
    }
    for (let i = 0; i < count; i++) {
      const cluster = clusterCenters[Math.floor(Math.random() * numClusters)];
      angles.push(cluster + (Math.random() - 0.5) * 1.2);
    }
    return angles;
  }

  // Primary neural pathways — main "dendrites" from core
  const pathways = [];
  const numPaths = 14;
  const pathAngles = clusterAngles(numPaths);
  for (let i = 0; i < numPaths; i++) {
    const baseAngle = pathAngles[i];
    const len = (50 + Math.random() * 140) * S;
    const cp1Dist = (25 + Math.random() * 50) * S;
    const cp1Angle = baseAngle + (Math.random() - 0.5) * 1.8;
    const cp2Dist = (50 + Math.random() * 60) * S;
    const cp2Angle = baseAngle + (Math.random() - 0.5) * 1.4;
    const endAngle = baseAngle + (Math.random() - 0.5) * 0.6;
    const wiggle1 = {
      freq: 0.4 + Math.random() * 0.8,
      amp: (8 + Math.random() * 18) * S,
      phase: Math.random() * Math.PI * 2,
    };
    const wiggle2 = {
      freq: 0.3 + Math.random() * 0.7,
      amp: (10 + Math.random() * 22) * S,
      phase: Math.random() * Math.PI * 2,
    };
    const wiggleEnd = {
      freq: 0.2 + Math.random() * 0.4,
      amp: (5 + Math.random() * 12) * S,
      phase: Math.random() * Math.PI * 2,
    };
    pathways.push({
      cp1xBase: cx + Math.cos(cp1Angle) * cp1Dist,
      cp1yBase: cy + Math.sin(cp1Angle) * cp1Dist,
      cp2xBase: cx + Math.cos(cp2Angle) * cp2Dist,
      cp2yBase: cy + Math.sin(cp2Angle) * cp2Dist,
      exBase: cx + Math.cos(endAngle) * len,
      eyBase: cy + Math.sin(endAngle) * len,
      w1: wiggle1,
      w2: wiggle2,
      wEnd: wiggleEnd,
      width: (0.6 + Math.random() * 1.4) * S,
      alphaBase: 0.08 + Math.random() * 0.22,
      speed: 0.3 + Math.random() * 0.8,
      phase: Math.random() * Math.PI * 2,
      fadeFreq: 0.1 + Math.random() * 0.25,
      fadePhase: Math.random() * Math.PI * 2,
      fadeMin: Math.random() * 0.3,
      pulses: [
        { speed: 0.4 + Math.random() * 0.6, phase: Math.random() * Math.PI * 2, size: (4 + Math.random() * 4) * S },
        { speed: 0.6 + Math.random() * 0.8, phase: Math.random() * Math.PI * 2, size: (3 + Math.random() * 3) * S },
      ],
      hue: Math.random() < 0.3 ? "purple" : Math.random() < 0.15 ? "green" : "blue",
    });
  }

  // Secondary branch pathways
  const branches = [];
  for (let i = 0; i < 10; i++) {
    const parent = pathways[Math.floor(Math.random() * pathways.length)];
    const forkT = 0.3 + Math.random() * 0.5;
    const branchAngle = Math.random() * Math.PI * 2;
    const branchLen = (20 + Math.random() * 50) * S;
    branches.push({
      parent,
      forkT,
      angle: branchAngle,
      len: branchLen,
      cpDist: (10 + Math.random() * 20) * S,
      cpAngle: branchAngle + (Math.random() - 0.5) * 1.5,
      width: (0.4 + Math.random() * 0.6) * S,
      alpha: 0.06 + Math.random() * 0.12,
      wiggle: {
        freq: 0.5 + Math.random() * 0.5,
        amp: (4 + Math.random() * 8) * S,
        phase: Math.random() * Math.PI * 2,
      },
      speed: 0.5 + Math.random() * 0.7,
      phase: Math.random() * Math.PI * 2,
    });
  }

  // Dashed outer orbits (elliptical)
  const orbits = [];
  for (let i = 0; i < 3; i++) {
    orbits.push({
      rx: (140 + i * 30) * S,
      ry: (80 + i * 25 + Math.random() * 30) * S,
      rot: ((i * 40 + Math.random() * 30 - 15) * Math.PI) / 180,
      speed: 0.08 + i * 0.03,
      phase: Math.random() * Math.PI * 2,
      dash: [8 * S, 6 * S],
      alpha: 0.06 + i * 0.02,
    });
  }

  // Particles
  const particles = [];
  for (let i = 0; i < 45; i++) {
    const angle = Math.random() * Math.PI * 2;
    const dist = (20 + Math.random() * 170) * S;
    particles.push({
      x: cx + Math.cos(angle) * dist,
      y: cy + Math.sin(angle) * dist,
      baseX: cx + Math.cos(angle) * dist,
      baseY: cy + Math.sin(angle) * dist,
      size: (0.6 + Math.random() * 2.2) * S,
      alpha: 0.1 + Math.random() * 0.55,
      driftX: (Math.random() - 0.5) * 14 * S,
      driftY: (Math.random() - 0.5) * 14 * S,
      speed: 0.4 + Math.random() * 1.8,
      phase: Math.random() * Math.PI * 2,
    });
  }

  function bezPt(sx, sy, c1x, c1y, c2x, c2y, ex, ey, t) {
    const u = 1 - t;
    return {
      x: u * u * u * sx + 3 * u * u * t * c1x + 3 * u * t * t * c2x + t * t * t * ex,
      y: u * u * u * sy + 3 * u * u * t * c1y + 3 * u * t * t * c2y + t * t * t * ey,
    };
  }

  function pathColor(hue, alpha) {
    if (hue === "purple")
      return {
        glow: "rgba(160,120,255," + alpha + ")",
        main: "rgba(180,150,255," + alpha + ")",
        bright: "rgba(210,190,255," + alpha + ")",
      };
    if (hue === "green")
      return {
        glow: "rgba(0,200,160," + alpha + ")",
        main: "rgba(80,230,180," + alpha + ")",
        bright: "rgba(150,255,220," + alpha + ")",
      };
    return {
      glow: "rgba(100,200,255," + alpha + ")",
      main: "rgba(130,210,255," + alpha + ")",
      bright: "rgba(180,235,255," + alpha + ")",
    };
  }

  let t = 0;
  let rafId;
  let running = true;

  function draw() {
    if (!running) return;
    const state = (stateRef && stateRef.current) || "idle";
    const sc = STATE_CONFIG[state] || STATE_CONFIG.idle;
    const speedMul = sc.speedMultiplier;
    const intensityMul = sc.intensityMultiplier;

    t += 0.016 * speedMul;
    ctx.clearRect(0, 0, W, H);

    // 1. Deep ambient glow
    const glow1 = ctx.createRadialGradient(cx, cy, 0, cx, cy, 200 * S);
    glow1.addColorStop(0, "rgba(15,60,150," + (0.3 + Math.sin(t * 0.8) * 0.08) * intensityMul + ")");
    glow1.addColorStop(0.4, "rgba(10,40,110,0.12)");
    glow1.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow1;
    ctx.fillRect(0, 0, W, H);

    // 2. Mid blue glow — breathes
    const breathe = Math.sin(t * 0.5) * 0.5 + 0.5;
    const glow2 = ctx.createRadialGradient(cx, cy, 0, cx, cy, (120 + breathe * 20) * S);
    glow2.addColorStop(0, "rgba(40,120,220," + (0.3 + breathe * 0.15) * intensityMul + ")");
    glow2.addColorStop(0.5, "rgba(30,90,200,0.08)");
    glow2.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow2;
    ctx.beginPath();
    ctx.arc(cx, cy, (120 + breathe * 20) * S, 0, Math.PI * 2);
    ctx.fill();

    // 3. Bright inner glow
    const glow3 = ctx.createRadialGradient(cx, cy, 0, cx, cy, 70 * S);
    glow3.addColorStop(0, "rgba(80,180,255," + (0.45 + Math.sin(t * 1.1 + 1) * 0.12) * intensityMul + ")");
    glow3.addColorStop(0.6, "rgba(50,140,240,0.1)");
    glow3.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow3;
    ctx.beginPath();
    ctx.arc(cx, cy, 70 * S, 0, Math.PI * 2);
    ctx.fill();

    // 4. Dashed outer orbits
    orbits.forEach((orb) => {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(orb.rot + t * orb.speed);
      ctx.setLineDash(orb.dash);
      ctx.strokeStyle = "rgba(80,170,240," + (orb.alpha + Math.sin(t + orb.phase) * 0.02) + ")";
      ctx.lineWidth = 0.8 * S;
      ctx.beginPath();
      ctx.ellipse(0, 0, orb.rx, orb.ry, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    });
    ctx.setLineDash([]);

    // 5. Neural pathways — curvas orgânicas animadas
    pathways.forEach((p) => {
      const fadeCycle = Math.sin(t * p.fadeFreq + p.fadePhase) * 0.5 + 0.5;
      const fadeAlpha = p.fadeMin + fadeCycle * (1 - p.fadeMin);
      const pulse = Math.sin(t * p.speed + p.phase) * 0.5 + 0.5;
      const a = p.alphaBase * (0.5 + pulse * 0.5) * fadeAlpha * intensityMul;
      if (a < 0.01) return;

      const cp1x = p.cp1xBase + Math.sin(t * p.w1.freq + p.w1.phase) * p.w1.amp;
      const cp1y = p.cp1yBase + Math.cos(t * p.w1.freq * 0.7 + p.w1.phase + 1) * p.w1.amp * 0.8;
      const cp2x = p.cp2xBase + Math.sin(t * p.w2.freq + p.w2.phase) * p.w2.amp;
      const cp2y = p.cp2yBase + Math.cos(t * p.w2.freq * 0.6 + p.w2.phase + 2) * p.w2.amp * 0.9;
      const ex = p.exBase + Math.sin(t * p.wEnd.freq + p.wEnd.phase) * p.wEnd.amp;
      const ey = p.eyBase + Math.cos(t * p.wEnd.freq * 0.8 + p.wEnd.phase) * p.wEnd.amp;

      const col = pathColor(p.hue, a);
      const dynWidth = p.width * (0.7 + pulse * 0.6);

      // Glow
      ctx.strokeStyle = pathColor(p.hue, a * 0.3).glow;
      ctx.lineWidth = (dynWidth + 5) * S;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ex, ey);
      ctx.stroke();

      // Main
      ctx.strokeStyle = col.main;
      ctx.lineWidth = dynWidth;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ex, ey);
      ctx.stroke();

      // Bright inner
      ctx.strokeStyle = pathColor(p.hue, a * 0.6).bright;
      ctx.lineWidth = Math.max(0.5, dynWidth * 0.35);
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ex, ey);
      ctx.stroke();

      // Traveling pulses
      p.pulses.forEach((pl) => {
        const tt = (((t * pl.speed + pl.phase) % 1) + 1) % 1;
        const pt = bezPt(cx, cy, cp1x, cp1y, cp2x, cp2y, ex, ey, tt);
        const intensity = Math.sin(tt * Math.PI);
        const pg = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, pl.size);
        pg.addColorStop(0, "rgba(220,245,255," + 0.8 * intensity * fadeAlpha * intensityMul + ")");
        pg.addColorStop(1, "rgba(100,200,255,0)");
        ctx.fillStyle = pg;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, pl.size, 0, Math.PI * 2);
        ctx.fill();
      });

      p._cp1x = cp1x;
      p._cp1y = cp1y;
      p._cp2x = cp2x;
      p._cp2y = cp2y;
      p._ex = ex;
      p._ey = ey;
      p._fadeAlpha = fadeAlpha;
    });

    // 5b. Branches
    branches.forEach((b) => {
      const par = b.parent;
      if (!par._fadeAlpha || par._fadeAlpha < 0.05) return;
      const forkPt = bezPt(cx, cy, par._cp1x, par._cp1y, par._cp2x, par._cp2y, par._ex, par._ey, b.forkT);
      const bex = forkPt.x + Math.cos(b.angle) * b.len + Math.sin(t * b.wiggle.freq + b.wiggle.phase) * b.wiggle.amp;
      const bey = forkPt.y + Math.sin(b.angle) * b.len + Math.cos(t * b.wiggle.freq * 0.7 + b.wiggle.phase) * b.wiggle.amp;
      const bcpx = forkPt.x + Math.cos(b.cpAngle) * b.cpDist;
      const bcpy = forkPt.y + Math.sin(b.cpAngle) * b.cpDist;

      const pulse = Math.sin(t * b.speed + b.phase) * 0.5 + 0.5;
      const a = b.alpha * (0.5 + pulse * 0.5) * par._fadeAlpha * intensityMul;

      ctx.strokeStyle = "rgba(130,210,255," + a + ")";
      ctx.lineWidth = b.width * (0.7 + pulse * 0.5);
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(forkPt.x, forkPt.y);
      ctx.quadraticCurveTo(bcpx, bcpy, bex, bey);
      ctx.stroke();

      ctx.fillStyle = "rgba(160,225,255," + a * 0.8 + ")";
      ctx.beginPath();
      ctx.arc(bex, bey, b.width * 1.5, 0, Math.PI * 2);
      ctx.fill();
    });

    // 6. Particles
    particles.forEach((p) => {
      const drift = Math.sin(t * p.speed + p.phase);
      p.x = p.baseX + p.driftX * drift;
      p.y = p.baseY + p.driftY * Math.cos(t * p.speed * 0.7 + p.phase);
      const a = p.alpha * (0.5 + Math.sin(t * p.speed + p.phase) * 0.5) * intensityMul;

      const pg = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
      pg.addColorStop(0, "rgba(120,200,255," + a * 0.4 + ")");
      pg.addColorStop(1, "rgba(80,160,240,0)");
      ctx.fillStyle = pg;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "rgba(160,225,255," + a + ")";
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    });

    // 7. Core halo — AMPLIADO (~2x) pra núcleo protagonista
    const thinkIntensity = (Math.sin(t * 0.7) * 0.5 + 0.5) * (Math.sin(t * 1.3) * 0.3 + 0.7);
    const haloScale = 1 + thinkIntensity * 0.3;
    const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, 56 * S * haloScale);
    halo.addColorStop(0, "rgba(160,230,255," + (0.5 + thinkIntensity * 0.25) * intensityMul + ")");
    halo.addColorStop(0.45, "rgba(80,180,255,0.15)");
    halo.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(cx, cy, 56 * S * haloScale, 0, Math.PI * 2);
    ctx.fill();

    // 8. Bright core — AMPLIADO: de 9*S pra 18*S (gradient) e 3.5*S pra 8*S (ponto branco)
    const coreScale = 1 + Math.sin(t * 1.3) * 0.15;
    const coreR = 18 * S * coreScale;
    const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
    core.addColorStop(0, "rgba(255,255,255,0.98)");
    core.addColorStop(0.25, "rgba(230,245,255,0.88)");
    core.addColorStop(0.55, "rgba(140,210,255,0.45)");
    core.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
    ctx.fill();

    // Ponto branco ultra-brilhante no centro — dobrou de tamanho
    ctx.fillStyle = "rgba(255,255,255," + (0.9 + Math.sin(t * 1.5) * 0.1) + ")";
    ctx.beginPath();
    ctx.arc(cx, cy, 8 * S * coreScale, 0, Math.PI * 2);
    ctx.fill();

    // Highlight interno do ponto branco (reforça a cor "acesa")
    ctx.fillStyle = "rgba(255,255,255," + (0.95 + Math.sin(t * 2) * 0.05) + ")";
    ctx.beginPath();
    ctx.arc(cx, cy, 4 * S * coreScale, 0, Math.PI * 2);
    ctx.fill();

    // 9. Lens flare
    const flareAlpha = 0.04 + Math.sin(t * 0.8) * 0.03;
    const flareW = 200 * S * (0.8 + Math.sin(t * 0.9) * 0.2);
    const flareGrad = ctx.createLinearGradient(cx - flareW, cy, cx + flareW, cy);
    flareGrad.addColorStop(0, "rgba(100,190,255,0)");
    flareGrad.addColorStop(0.3, "rgba(150,220,255," + flareAlpha + ")");
    flareGrad.addColorStop(0.5, "rgba(255,255,255," + flareAlpha * 0.7 + ")");
    flareGrad.addColorStop(0.7, "rgba(150,220,255," + flareAlpha + ")");
    flareGrad.addColorStop(1, "rgba(100,190,255,0)");
    ctx.fillStyle = flareGrad;
    ctx.fillRect(cx - flareW, cy - 1.5 * S, flareW * 2, 3 * S);

    rafId = requestAnimationFrame(draw);
  }

  draw();

  return () => {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
  };
}

const NexoCore = memo(function NexoCore({
  state = "idle",
  size = 320,
  onClick,
  className = "",
}) {
  const canvasRef = useRef(null);
  const stateRef = useRef(state);

  // Atualiza a ref sem reiniciar o canvas
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const scale = size / 420; // escala pro canvas 840x840 ficar proporcional ao tamanho pedido
    const cleanup = initNeuralOrb(canvasRef.current, {
      scale: 2 * scale, // 2 é o fator usado na landing page original pro orb grande
      stateRef,
    });
    return cleanup;
  }, [size]);

  const cfg = STATE_CONFIG[state] || STATE_CONFIG.idle;
  const containerVibrate = cfg.vibrate
    ? { x: [0, -1, 1, -0.7, 0.7, 0], y: [0, 0.5, -0.5, 0.4, -0.4, 0] }
    : { x: 0, y: 0 };

  return (
    <motion.div
      className={`relative select-none ${onClick ? "cursor-pointer" : "pointer-events-none"} ${className}`}
      style={{ width: size, height: size }}
      onClick={onClick}
      animate={containerVibrate}
      transition={cfg.vibrate ? { duration: 0.4, repeat: Infinity, ease: "easeInOut" } : {}}
      data-testid="nexo-core"
    >
      <motion.canvas
        ref={canvasRef}
        width={840}
        height={840}
        className="w-full h-full block"
        style={
          cfg.filterStatic
            ? { filter: cfg.filterStatic, transition: "filter 0.5s ease" }
            : undefined
        }
        animate={
          cfg.filterAnim
            ? { filter: cfg.filterAnim.values }
            : undefined
        }
        transition={
          cfg.filterAnim
            ? {
                duration: cfg.filterAnim.duration,
                repeat: Infinity,
                ease: "easeInOut",
              }
            : undefined
        }
      />
    </motion.div>
  );
});

export default NexoCore;
